import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';

/**
 * Subscription Bypass Interceptor
 *
 * DUAL PURPOSE:
 * 1. WORKAROUND for SubId interceptor blocking issue in @pa-angular/pa-fe-boilerplate
 * 2. ADK API subscription-id header injection for chat routes
 *
 * Problem:
 * The SubIdInterceptor returns EMPTY when no subscription_id query parameter exists,
 * which blocks HTTP requests. This prevents fetching subscriptions and entitlements
 * on the workspace launcher page (which intentionally has no subscription_id).
 *
 * Solution:
 * Add an empty subscription-id header to bypass the SubId interceptor's blocking logic.
 * The SubId interceptor checks for header existence via hasSubscriptionIdHeader() and
 * skips blocking when the header is present (even if empty).
 *
 * This interceptor MUST run BEFORE the SubId interceptor in the chain.
 *
 * See: docs/bmad-archive/implementation-artifacts/library-issues/subid-interceptor-subscription-fetch-blocking.md
 *
 * ADK API Integration:
 * For ADK API routes (apps/*, run, run_sse), inject subscription-id header from query params.
 * Backend uses this header for data isolation across subscriptions.
 */
@Injectable()
export class SubscriptionBypassInterceptor implements HttpInterceptor {
  private readonly router = inject(Router);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Skip interception for absolute URLs (e.g., feedback endpoint to localhost mock server)
    // The baseurl interceptor should not prepend to absolute URLs
    if (req.url.startsWith('http://') || req.url.startsWith('https://')) {
      return next.handle(req);
    }

    // Bypass for subscription list fetch (no specific subscription ID in URL)
    // Example: GET /services/subscriptions
    if (
      req.url.includes('/services/subscriptions') &&
      !req.url.match(/\/services\/subscriptions\/[^/]+/)
    ) {
      const modifiedReq = req.clone({
        headers: req.headers.set('subscription-id', ''),
      });
      return next.handle(modifiedReq);
    }

    // Bypass for entitlements fetch when on workspace launcher page
    // Example: GET /services/subscriptions/sub_xxx/entitlements
    // These requests have a subscription ID in the URL path, but we're fetching them
    // progressively before the user has selected a workspace (no subscription_id in query params)
    if (req.url.includes('/entitlements') && req.method === 'GET') {
      // Extract subscription ID from URL path
      const match = req.url.match(/\/subscriptions\/([^/]+)\/entitlements/);
      const subscriptionId = match ? match[1] : '';

      const modifiedReq = req.clone({
        headers: req.headers.set('subscription-id', subscriptionId),
      });
      return next.handle(modifiedReq);
    }

    // ADK API routes - inject subscription-id header from query params
    // Example routes: apps/{app}/users/{user}/sessions, run, run_sse, registry
    if (this.isAdkApiRoute(req.url)) {
      const subscriptionId = this.getSubscriptionIdFromQueryParams();

      if (subscriptionId) {
        const modifiedReq = req.clone({
          headers: req.headers.set('subscription-id', subscriptionId),
        });
        return next.handle(modifiedReq);
      } else {
        console.warn(
          `[SubscriptionBypassInterceptor] ADK route ${req.url} called without subscription_id in query params`,
        );
      }
    }

    // Pass through all other requests unchanged
    return next.handle(req);
  }

  /**
   * Check if URL is an ADK API route
   */
  private isAdkApiRoute(url: string): boolean {
    return (
      url.includes('/apps/') ||
      url.includes('/run') ||
      url.includes('/run_sse') ||
      url.includes('/registry')
    );
  }

  /**
   * Get subscription_id from query parameters using Router
   *
   * Uses Router instead of ActivatedRoute to avoid staleness issues.
   * Router.url always reflects the current URL, unlike ActivatedRoute which
   * can become stale in singleton services like interceptors.
   */
  private getSubscriptionIdFromQueryParams(): string | null {
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);
    return urlTree.queryParams['subscription_id'] || null;
  }
}
